import { supabase } from './supabaseService';
import { User, Role } from '../types/multiTenant';

/**
 * User Service
 * Handles all user-related operations with Supabase
 */
class UserService {
  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          role:roles(*),
          organization:organizations(*)
        `)
        .eq('id', userId)
        .eq('deleted', false)
        .single();

      if (error) {
        console.error('Error fetching user:', error);
        return null;
      }

      return this.transformUser(data);
    } catch (error) {
      console.error('Error in getUserById:', error);
      return null;
    }
  }

  /**
   * Get user by email
   */
  async getUserByEmail(email: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          role:roles(*),
          organization:organizations(*)
        `)
        .eq('email', email)
        .eq('deleted', false)
        .single();

      if (error) {
        console.error('Error fetching user by email:', error);
        return null;
      }

      return this.transformUser(data);
    } catch (error) {
      console.error('Error in getUserByEmail:', error);
      return null;
    }
  }

  /**
   * Create new user
   */
  async createUser(data: {
    email: string;
    fullName: string;
    organizationId: string;
    roleId: string;
    invitedBy?: string;
  }): Promise<User | null> {
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .insert({
          email: data.email,
          full_name: data.fullName,
          organization_id: data.organizationId,
          role_id: data.roleId,
          invited_by: data.invitedBy,
          is_active: true,
        })
        .select(`
          *,
          role:roles(*),
          organization:organizations(*)
        `)
        .single();

      if (error) {
        console.error('Error creating user:', error);
        return null;
      }

      return this.transformUser(userData);
    } catch (error) {
      console.error('Error in createUser:', error);
      return null;
    }
  }

  /**
   * Update user
   */
  async updateUser(userId: string, updates: Partial<User>): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          full_name: updates.fullName,
          avatar_url: updates.avatarUrl,
          phone: updates.phone,
          role_id: updates.roleId,
          is_active: updates.isActive,
          preferences: updates.preferences,
        })
        .eq('id', userId)
        .select(`
          *,
          role:roles(*),
          organization:organizations(*)
        `)
        .single();

      if (error) {
        console.error('Error updating user:', error);
        return null;
      }

      return this.transformUser(data);
    } catch (error) {
      console.error('Error in updateUser:', error);
      return null;
    }
  }

  /**
   * Update user last login
   */
  async updateLastLogin(userId: string): Promise<void> {
    try {
      await supabase
        .from('users')
        .update({
          last_login_at: new Date().toISOString(),
        })
        .eq('id', userId);
    } catch (error) {
      console.error('Error updating last login:', error);
    }
  }

  /**
   * Deactivate user (soft delete)
   */
  async deactivateUser(userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          is_active: false,
          deleted: true,
        })
        .eq('id', userId);

      if (error) {
        console.error('Error deactivating user:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deactivateUser:', error);
      return false;
    }
  }

  /**
   * Invite user to organization
   */
  async inviteUser(data: {
    email: string;
    fullName: string;
    organizationId: string;
    roleId: string;
    invitedBy: string;
  }): Promise<User | null> {
    try {
      // Check if user already exists
      const existing = await this.getUserByEmail(data.email);
      if (existing) {
        console.error('User already exists');
        return null;
      }

      // Create user with pending invitation
      const { data: userData, error } = await supabase
        .from('users')
        .insert({
          email: data.email,
          full_name: data.fullName,
          organization_id: data.organizationId,
          role_id: data.roleId,
          invited_by: data.invitedBy,
          is_active: false, // Inactive until invitation accepted
        })
        .select(`
          *,
          role:roles(*),
          organization:organizations(*)
        `)
        .single();

      if (error) {
        console.error('Error inviting user:', error);
        return null;
      }

      return this.transformUser(userData);
    } catch (error) {
      console.error('Error in inviteUser:', error);
      return null;
    }
  }

  /**
   * Accept invitation
   */
  async acceptInvitation(userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          is_active: true,
          invitation_accepted_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        console.error('Error accepting invitation:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in acceptInvitation:', error);
      return false;
    }
  }

  /**
   * Get role by name for an organization
   */
  async getRoleByName(organizationId: string, roleName: string): Promise<Role | null> {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('name', roleName)
        .eq('deleted', false)
        .single();

      if (error) {
        console.error('Error fetching role:', error);
        return null;
      }

      return this.transformRole(data);
    } catch (error) {
      console.error('Error in getRoleByName:', error);
      return null;
    }
  }

  /**
   * Get all roles for an organization
   */
  async getOrganizationRoles(organizationId: string): Promise<Role[]> {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .order('name');

      if (error) {
        console.error('Error fetching roles:', error);
        return [];
      }

      return (data || []).map(item => this.transformRole(item));
    } catch (error) {
      console.error('Error in getOrganizationRoles:', error);
      return [];
    }
  }

  /**
   * Change user password
   * Uses Supabase Auth for password management
   */
  async changePassword(
    userId: string,
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // First, verify the current user is authenticated
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        return { success: false, error: 'Usuário não autenticado' };
      }

      // Verify that the user is changing their own password
      if (user.id !== userId) {
        return { success: false, error: 'Não autorizado' };
      }

      // Validate new password
      if (newPassword.length < 6) {
        return { success: false, error: 'A nova senha deve ter pelo menos 6 caracteres' };
      }

      // Use Supabase Auth to update password
      // Note: Supabase handles current password verification internally
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        console.error('Error changing password:', updateError);
        return {
          success: false,
          error: updateError.message || 'Erro ao alterar senha'
        };
      }

      // Update the user's updated_at timestamp
      await supabase
        .from('users')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', userId);

      return { success: true };
    } catch (error) {
      console.error('Error in changePassword:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao alterar senha'
      };
    }
  }

  /**
   * Get users by role ID
   * Used to check if a role can be deleted
   */
  async getUsersByRole(roleId: string): Promise<User[]> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('role_id', roleId)
        .eq('deleted', false);

      if (error) {
        console.error('Error fetching users by role:', error);
        return [];
      }

      return (data || []).map(item => this.transformUser(item));
    } catch (error) {
      console.error('Error in getUsersByRole:', error);
      return [];
    }
  }

  /**
   * Transform database user to app format
   */
  private transformUser(data: any): User {
    return {
      id: data.id,
      email: data.email,
      organizationId: data.organization_id,
      roleId: data.role_id,
      fullName: data.full_name,
      avatarUrl: data.avatar_url,
      phone: data.phone,
      isActive: data.is_active,
      invitedBy: data.invited_by,
      invitationAcceptedAt: data.invitation_accepted_at,
      preferences: data.preferences || {},
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      deleted: data.deleted || false,
      lastLoginAt: data.last_login_at,
    };
  }

  /**
   * Transform database role to app format
   */
  private transformRole(data: any): Role {
    return {
      id: data.id,
      organizationId: data.organization_id,
      name: data.name,
      displayName: data.display_name,
      permissions: data.permissions || {},
      isSystemRole: data.is_system_role,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      deleted: data.deleted || false,
    };
  }
}

export const userService = new UserService();
export default userService;
